import React, { useState, useEffect, useRef } from "react";
import styled from "@emotion/styled";

import LayerWrapper from "~/components/Board/layers/LayerWrapper";
import matrix from "~/components/Board/matrix";
import { clamp } from "~/core/scripts/chart-components/utils";
import { is768Query } from "~/core/scripts/mediaquery-helper";

const noop = () => {};

import {
  initialDrawState,
  canMove,
  isMaxReached,
  getResult,
  getBidenOverallVotes,
  getTrumpOverallVotes,
  isTrumpOverallWinner,
  isImpossible,
} from "./utils";

const ItemsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  position: relative;
  height: 100%;
  width: 100%;
  user-select: none;
`;

const itemSize = (1 / matrix[0].length) * 100;

const DrawItem = styled.div`
  height: ${itemSize}%;
  width: ${itemSize}%;
  background: ${(p) => (p.isActive ? "rgba(0, 0, 0, 0.15)" : "transparent")};
  border: 1px solid #fff;
  display: flex;
  justify-content: center;
  align-items: center;
  touch-action: none;
  position: relative;
`;

const ResultItem = styled(DrawItem)`
  background: ${(p) =>
    p.isBiden
      ? p.theme.colors.democraticReduced
      : p.theme.colors.republicanReduced};
  position: absolute;
  top: ${(p) => p.top}%;
  left: ${(p) => p.left}%;
  z-index: 9;

  &:before {
    content: "";
    position: absolute;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
    border-style: solid;
    border-width: ${(p) => (p.small ? 2 : 3)}px;
    border-color: #111;
    border-color: ${(p) =>
      p.isBiden ? p.theme.colors.democratic : p.theme.colors.republican};
  }
  ${({ hasTop, small }) =>
    hasTop &&
    `
    border-top-color: transparent;
    &:before {
      border-top-width: 0;
      top: -${small ? 4 : 5}px;
    }
  `}
  ${({ hasRight, small }) =>
    hasRight &&
    `
    border-right-color: transparent;
    &:before {
      border-right-width: 0;
      right: -${small ? 4 : 5}px;
    }
  `}
  ${({ hasBottom, small }) =>
    hasBottom &&
    `
    border-bottom-color: transparent;
    &:before {
      border-bottom-width: 0;
      bottom: -${small ? 4 : 5}px;
    }
  `}
  ${({ hasLeft, small }) =>
    hasLeft &&
    `
    border-left-color: transparent;
    &:before {
      border-left-width: 0;
      left: -${small ? 4 : 5}px;
    }
  `}

  ${(p) =>
    !p.hasLeft &&
    !p.hasTop &&
    p.showNumber &&
    p.theme.snippets.bulletedAfter(p.number)}
`;

const ResultItems = ({ items, number, small, hideNumbers = false }) => {
  const isBiden =
    items.reduce((acc, curr) => (matrix[curr.x][curr.y] ? acc + 1 : acc), 0) >=
    2;
  return items.map((item, i) => {
    const hasTop = !!items.find((it) => it.y === item.y && it.x === item.x - 1);
    const hasBottom = !!items.find(
      (it) => it.y === item.y && it.x === item.x + 1
    );
    const hasLeft = !!items.find(
      (it) => it.x === item.x && it.y === item.y - 1
    );
    const hasRight = !!items.find(
      (it) => it.x === item.x && it.y === item.y + 1
    );

    return (
      <ResultItem
        key={`result-item-${item.x}-${item.y}`}
        hasLeft={hasLeft}
        hasRight={hasRight}
        hasTop={hasTop}
        hasBottom={hasBottom}
        top={item.x * (1 / 3) * 100}
        left={item.y * (1 / 3) * 100}
        isBiden={isBiden}
        number={number}
        showNumber={i === 0 && !hideNumbers}
        small={small}
      />
    );
  });
};

const DrawLayer = ({
  onResult = noop,
  onError = noop,
  resultState,
  setResultState = noop,
  small = false,
  hideNumbers = false,
}) => {
  const [isDrawing, setDrawing] = useState(false);
  const [drawState, setDrawState] = useState(initialDrawState);
  const wrapperRef = useRef();
  const isDesktop = is768Query();

  const setItem = (x, y, active) => {
    const nextDrawState = drawState.map((r, dx) =>
      r.map((c, dy) => (dx === x && dy === y ? active : c))
    );
    nextDrawState.last = { x, y };
    setDrawState(nextDrawState);
  };

  const handleDrawStart = (x, y) => {
    setDrawing(true);

    if (canMove(x, y, drawState, resultState)) {
      setItem(x, y, true);
    }
  };

  const handleTouchStart = (x, y) => {
    handleDrawStart(x, y);
  };

  const handleDrawEnd = () => {
    setDrawing(false);

    const result = getResult(drawState);

    if (result.length === 3 && resultState.length < 3) {
      setResultState((rs) => rs.concat([result]));
    }

    setDrawState(initialDrawState);
  };

  const handleItemEnter = (x, y) => {
    if (isMaxReached(drawState)) {
      return handleDrawEnd();
    }

    if (!canMove(x, y, drawState, resultState) || !isDrawing) {
      return false;
    }

    setItem(x, y, true);
  };

  const handleTouchMove = (evt) => {
    const bbox = wrapperRef.current.getBoundingClientRect();
    const x = evt.touches[0].clientX - bbox.x;
    const y = evt.touches[0].clientY - bbox.y;

    const py = clamp(Math.round((x / bbox.width) * 2), 0, 2);
    const px = clamp(Math.round((y / bbox.height) * 2), 0, 2);

    if (
      drawState.last &&
      (drawState.last.x !== px || drawState.last.y !== py)
    ) {
      handleItemEnter(px, py);
    }
  };

  useEffect(() => {
    if (isImpossible(resultState)) {
      onError();
    }

    const bidenOverallVotes = getBidenOverallVotes(resultState);
    const trumpOverallVotes = getTrumpOverallVotes(resultState);

    const trumpWon =
      resultState.length == 3 && isTrumpOverallWinner(resultState);
    const bidenWon = resultState.length == 3 && !trumpWon;

    onResult({ bidenOverallVotes, trumpOverallVotes, trumpWon, bidenWon });
  }, [resultState]);

  return (
    <LayerWrapper>
      <ItemsWrapper
        onMouseUp={handleDrawEnd}
        onTouchEnd={handleDrawEnd}
        onTouchMove={(evt) => handleTouchMove(evt)}
        ref={wrapperRef}
      >
        {drawState.map((row, x) =>
          row.map((col, y) => (
            <DrawItem
              key={`result-item-${x}-${y}`}
              onMouseDown={() => handleDrawStart(x, y)}
              onTouchStart={() => handleDrawStart(x, y)}
              onMouseEnter={() => handleItemEnter(x, y)}
              isActive={col}
            />
          ))
        )}
        {resultState.map((result, i) => (
          <ResultItems
            key={`result-items-${i}`}
            number={i + 1}
            items={result}
            small={small || !isDesktop}
            hideNumbers={hideNumbers}
          />
        ))}
      </ItemsWrapper>
    </LayerWrapper>
  );
};

export default DrawLayer;
